import io, { Socket } from 'socket.io-client';
import { MediaEngine, MediaEngineOpts } from './mediaEngine';
import { BrowserMediaEngine } from './browserMediaEngine';
import { NativeMediaEngine, nativeMediaAvailable } from './nativeMediaEngine';
import { isNativeShell } from './nativeCall';
import { probeSetShell } from './mediaProbe';
import { gainAuditCommand, gainAuditUndelivered, gainAuditDelivered, GainAuditRow } from './gainAudit';

/** Pipeline B device client.
 *
 * Browser/device calls go through Flask's authenticated Socket.IO front door on a
 * B-only namespace. Socket.IO carries auth, signalling and control. Media moves on
 * Media rides RTP audio tracks; the data channel carries CONTROL only.
 * edge while the daemon stays loopback-only.
 */

export type VoiceDaemonState =
  | 'idle'
  | 'connecting'
  | 'listening'
  | 'user_speaking'
  | 'thinking'
  | 'agent_speaking'
  | 'ending'
  | 'error';

/** What the box says is true about the call. A window renders this and nothing else. */
export interface VoiceCall {
  call_id: string;
  domain: string;
  agent: string;
  since?: number;
  has_media?: boolean;
}

export type VoiceDaemonEvent =
  | { type: 'session_ready'; session_id: string; model: string;
      input_source?: string; output_target?: string;
      /** AUTHORITATIVE — the pair the box actually has, never the one requested. */
      domain?: string; agent?: string }
  | { type: 'transcript_partial'; text: string }
  | { type: 'transcript_final'; text: string; turn_key?: string }
  | { type: 'agent_speaking_start'; turn_key: string; sentence_idx: number;
      agent_text: string; overlap?: boolean }
  | { type: 'agent_speaking_end'; turn_key: string; final_sentence_idx: number;
      delivered_sentence_count: number }
  | { type: 'agent_switched'; domain: string; agent: string; turn_key?: string }
  | { type: 'user_barge_in'; turn_key?: string }
  | { type: 'mute_state'; muted: boolean; standing_s?: number }
  | { type: 'transport_state'; state: string; transport?: string; detail?: string }
  | { type: 'session_ended'; reason: string }
  /** Another window attached to this call. THIS WINDOW MUST STOP — it does not
   *  recover, retry or resume. Two windows recovering against each other is the loop. */
  | { type: 'session_superseded'; call_id: string; reason: string }
  /** The box telling every window what is true, the moment it becomes true. A window
   *  never sits showing "no call" while a call is live. */
  | { type: 'call_state'; call: VoiceCall | null }
  | { type: 'error'; code: string | number; turn_key?: string; reason?: string;
      detail?: string; waited_s?: number };

interface DaemonSocketOpts {
  domain: string;
  agent: string;
  onEvent: (e: VoiceDaemonEvent) => void;
  onClose: () => void;
  /**
   * OWNERSHIP, HANDED OVER BEFORE ANYTHING CAN LEAK (Item 2, owner-ruled 2026-07-30).
   *
   * connect() used to construct the wrapper and then await startSession() and
   * negotiateWebRtc() before returning it — and negotiateWebRtc opens the microphone
   * and then waits up to 15s for the server's answer. If that hung or threw, the
   * wrapper was never returned, so the caller's cleanup had nothing to close and the
   * device microphone stayed live with NOTHING OWNING IT. That is exactly the iOS
   * "stop audio recording" state the owner saw: a capture running with no session.
   *
   * The wrapper is now handed to the caller the instant it exists — before the socket
   * connects, and two awaits before the mic is opened. The mic can never outlive the
   * thing that opened it, because the thing that opened it is owned first.
   */
  onCreated?: (socket: VoiceDaemonSocket) => void;
  /**
   * Does this window intend to START a call, or only to ASK whether one exists?
   *
   * A tap creates (`true`, the default). A window merely OPENING asks (`false`) — it
   * never asserts a call it did not start, and it never creates one by appearing. If
   * there is no call, an asking window changes nothing and getCall() stays null.
   */
  createIfAbsent?: boolean;
  /**
   * Fired the instant the box answers "yes, there is a call" — BEFORE attaching or
   * negotiating. Asking is a read and must not occupy a visible state, but once the
   * answer is known the sphere legitimately means "there is a call", and the surface
   * needs that moment rather than the one several awaits later.
   */
  onCallDiscovered?: (call: VoiceCall) => void;
  /** The microphone level, where the engine has no MediaStream to hand over. */
  onLevel?: (level: number) => void;
}

const NAMESPACE = '/voice-b';
const RECONNECT_DELAY_MAX_MS = 10_000;
/**
 * THERE IS NO CLIENT-OWNED SESSION KEY ANY MORE (owner ruling 2026-07-30).
 *
 * This used to be `rexy_voice_b_client_session_id` in sessionStorage — the browser
 * minted the identity of the box's call and kept it in the document's pocket.
 * sessionStorage survives reload, is COPIED by tab duplication and is SHARED by
 * same-origin iframes, so any context that inherited the token was handed the live
 * call. That is what let two windows claim one session and tear each other's peer
 * down on 2026-07-30.
 *
 * The call lives on the box, so the box mints and owns its id. A window learns the
 * call_id from the server's reply and holds it IN MEMORY for its own lifetime only —
 * that is knowledge about the call, not ownership of it. A window is disposable.
 */
export function getAuthToken(): string {
  return (window as any).__rexyToken || '';
}

export function forceAuthLost(expectedToken?: string): void {
  if (expectedToken !== undefined) {
    const current = getAuthToken();
    if (current && current !== expectedToken) return;
  }
  try { window.dispatchEvent(new CustomEvent('rexy:auth-lost')); } catch {}
}

function socketOrigin(): string {
  return window.location.protocol === 'https:'
    ? `wss://${window.location.host}`
    : `ws://${window.location.hostname}:5000`;
}

export async function connectVoiceDaemon(opts: DaemonSocketOpts): Promise<VoiceDaemonSocket> {
  const token = getAuthToken();
  if (!token) {
    forceAuthLost();
    throw new Error('not_authenticated');
  }
  return VoiceDaemonSocket.connect(token, opts);
}

export class VoiceDaemonSocket {
  private socket: Socket;
  private opts: DaemonSocketOpts;
  private token: string;
  /** Box-minted, learned from the server, held in memory for this window's lifetime.
   *  Never persisted — persistence is what made a document the owner of the call. */
  private callId: string | null = null;
  /** The box's own description of the call. Rendered as-is; never a local guess. */
  private call: VoiceCall | null = null;
  private readonly createIfAbsent: boolean;
  /** Set when the box says another window took the call. Blocks every resume path. */
  private superseded = false;
  private reconnectTimer: number | null = null;
  private started = false;
  /**
   * AN END IS SITTING IN THE SOCKET'S BUFFER, WAITING FOR A RECONNECT.
   *
   * The tap path has four links — emit, reconnect, flush, and the box's answer —
   * and on 2026-08-05 the box's audit could prove the first three and the surface
   * could report none of them. The owner was left reading "reconnecting to tell
   * it" with no way to know which link had stalled. This one flag makes the
   * device's half of that visible: it is set when an end is buffered rather than
   * sent, and cleared at the exact moment socket.io flushes it.
   */
  private endPending = false;
  private closing = false;
  private negotiating = false;
  private negotiatePromise: Promise<void> | null = null;
  private abortWaiters: Array<(e: Error) => void> = [];
  /**
   * THE MEDIA, BEHIND THE SEAM (stage 3a).
   *
   * Selected in ONE place. Today it is always the browser engine — the native
   * path is 3b and is deliberately not wired, so this construction is the only
   * line that changes when it is. The engine is handed the session's own
   * liveness rules rather than re-deriving them, which is what keeps the
   * cancellation stages and the `closing` guards identical to before the lift.
   */
  private engine: MediaEngine;
  /**
   * IS THIS SESSION AUDITING THE GAIN (build 31)?
   *
   * True only where the native engine runs, and taken from the SAME selection
   * that chose the engine — not a second call to `nativeMediaAvailable()`, which
   * would be a second opinion about the device and could disagree with the first.
   *
   * DESKTOP UNCHANGED IN BEHAVIOUR, not merely in constants: on the browser path
   * this is false, so no gain row is ever composed and nothing new goes on the
   * desktop's wire. The mute commands below share the flag with the engine rows
   * for exactly that reason — `mute()` is common to both paths and would
   * otherwise be the one site that leaked this round onto the desktop.
   */
  private readonly gainAudited: boolean;

  private constructor(socket: Socket, token: string, opts: DaemonSocketOpts) {
    this.socket = socket;
    this.token = token;
    this.opts = opts;
    this.createIfAbsent = opts.createIfAbsent !== false;
    // ── THE ONE SELECTION SITE (stage 3b) ─────────────────────────────────
    // Native media where the plugin exists, the proven browser path everywhere
    // else. DESKTOP IS UNREACHABLE FROM HERE: nativeMediaAvailable() is
    // Capacitor.isNativePlatform() && the plugin being registered, both false in
    // any browser, so the desktop path is selected by construction rather than
    // by a flag someone could set wrongly.
    const engineOpts: MediaEngineOpts = {
      onTransport: (e) => this.opts.onEvent({ type: 'transport_state', ...e }),
      // THE ENGINE STATES THE FACT; THIS PUTS IT ON THE WIRE. A missing assertion
      // is not cosmetic — the box refuses to commit a mark as delivered until a
      // client asserts playback, so speech the owner heard would be lost from the
      // transcript, silently.
      onPlaybackAssert: (a) => this.socket.emit('voice:playback_blocked', a),
      // AN ASSERTION THAT NEVER LEFT THE DEVICE (2026-08-10). Audit-only at the
      // box: it changes no state, because the state it would change is the one
      // that correctly never moved. It exists so the box's own record can say
      // WHY nothing was acked, instead of the absence of a row being the only
      // evidence — which is what made the last failure take a full round to
      // read. Rides this window's existing socket; no second client, no new
      // verb on the media channel.
      onPlaybackUndelivered: (detail) =>
        this.socket.emit('voice:playback_undelivered', { detail }),
      // THE GAIN AUDIT (build 31). Same road as the line above and for the same
      // reason: it rides THIS WINDOW'S EXISTING SOCKET. No second client, no
      // auth of its own, no call-id contract, and NO NEW VERB on the media
      // control channel — the inbound set there stays {end, mute, playing} and
      // the outbound {call_ended, mute_state}. The engine states the row; the
      // caller owns the wire.
      onGainAudit: (row) => this.sendGainAudit(row),
      onResumeNeeded: (reason) => this.scheduleTransportResume(reason),
      onMicUnavailable: (detail) => this.opts.onEvent({
        type: 'error',
        code: 'DEVICE_MIC_UNAVAILABLE',
        reason: 'the device microphone could not be opened',
        detail,
      }),
      onLevel: (level: number) => { this.opts.onLevel?.(level); },
      assertLive: (stage: string) => this.assertLive(stage),
      isClosing: () => this.closing,
    };
    // THE DEVICE FACT, STATED WHERE THE ENGINE FACT IS DECIDED. The probe labels
    // every reading with the engine that produced it; this is the other half —
    // which device it ran on. It is set HERE, at the one site that knows both,
    // so the two can never disagree about what ran where. The probe is not
    // allowed to work it out for itself: mediaProbe.ts has no imports, and
    // reaching for isNativeShell() there would put Capacitor behind the desktop
    // engine, which is the seam this round is closing rather than widening.
    probeSetShell(isNativeShell() ? 'ios' : 'web');
    this.engine = nativeMediaAvailable()
      ? new NativeMediaEngine(engineOpts)
      : new BrowserMediaEngine(engineOpts);
    // ── READ OFF THE ENGINE THAT WAS ACTUALLY BUILT (build 31) ────────────
    // Not a second `nativeMediaAvailable()` call. That would be a second opinion
    // about the device, evaluated separately, and two opinions can disagree —
    // which is how a session ends up auditing a native gain that no native
    // engine is producing. This asks the object that exists.
    this.gainAudited = this.engine instanceof NativeMediaEngine;
    this.wire();
  }

  static async connect(token: string, opts: DaemonSocketOpts): Promise<VoiceDaemonSocket> {
    const sock = io(`${socketOrigin()}${NAMESPACE}`, {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelayMax: RECONNECT_DELAY_MAX_MS,
      timeout: 10000,
      forceNew: true,
    });
    const wrapper = new VoiceDaemonSocket(sock, token, opts);
    // OWNERSHIP FIRST — before the connect await, before the mic exists.
    try { opts.onCreated?.(wrapper); } catch {}
    try {
      await new Promise<void>((resolve, reject) => {
        const onConnect = () => {
          sock.off('connect_error', onError);
          wrapper.releaseAbort(reject);
          resolve();
        };
        const onError = (e: any) => {
          sock.off('connect', onConnect);
          wrapper.releaseAbort(reject);
          opts.onEvent({ type: 'error', code: 0, reason: 'connect_error', detail: String(e?.message ?? e) });
          reject(e);
        };
        sock.once('connect', onConnect);
        sock.once('connect_error', onError);
        // A cancel while the socket is still connecting must not wait out the 10s
        // timeout — close() rejects this directly.
        wrapper.registerAbort(reject);
      });
      wrapper.assertLive('connect');
      let target = { domain: opts.domain, agent: opts.agent };
      if (opts.createIfAbsent === false) {
        // THE WINDOW ASKS. It does not assert a call it never started — a brand-new
        // window has no peer and no call id, and requiring those is what made opening
        // an app end a call. Read-only: if there is nothing, nothing has changed.
        const live = await wrapper.getCallState();
        wrapper.assertLive('get_call_state');
        // NO CALL: the wrapper is returned STILL CONNECTED, deliberately. It stays as
        // glass so the box's voice:call_state broadcast reaches it the moment a call
        // starts — otherwise this window would sit showing "no call" while one ran.
        if (!live) return wrapper;
        try { opts.onCallDiscovered?.(live); } catch {}
        // Attach under the CALL'S OWN pair, never this window's idea of one.
        target = { domain: live.domain, agent: live.agent };
      }
      const attach = await wrapper.startSession(target.domain, target.agent);
      wrapper.assertLive('session_start');
      // keep_media: this window already carries the call's audio, so there is nothing
      // to negotiate. Otherwise it takes the media onto its own mic and speakers — the
      // incumbent peer is swapped atomically at the box, never killed in advance.
      if (attach.call && !attach.keepMedia) {
        await wrapper.negotiateWebRtc();
        wrapper.assertLive('negotiate');
      } else if (attach.call) {
        // ADOPTED WITH THE MEDIA KEPT — no ontrack fires, so nothing re-runs
        // attachPlayback. The box un-asserted playback when this window detached, so
        // without re-stating it here the track would stay in not-yet-asserted and every
        // mark would go un-acked: she would speak, this window would play it, and none
        // of it would be recorded as heard.
        wrapper.reassertPlayback();
      }
    } catch (e) {
      // SELF-TEARDOWN ON ANY THROW. Even with onCreated the caller may not yet have
      // wired its own cleanup, and a half-built session must never be left holding the
      // device. close() stops the mic, closes the peer and drops the socket.
      try { wrapper.close(); } catch {}
      throw e;
    }
    return wrapper;
  }

  /** Throws if this wrapper was closed/cancelled — checked after EVERY await on the
   *  start path, so a late-resolving step cannot reanimate a dead session. */
  assertLive(stage: string): void {
    if (this.closing) throw new Error(`voice_start_cancelled:${stage}`);
  }

  registerAbort(reject: (e: Error) => void): void {
    this.abortWaiters.push(reject);
  }

  releaseAbort(reject: (e: Error) => void): void {
    this.abortWaiters = this.abortWaiters.filter((r) => r !== reject);
  }

  private wire(): void {
    this.socket.on('connect', () => {
      // THE FLUSH IS A FACT, AND IT IS KNOWABLE HERE. socket.io's onconnect runs
      // emitBuffered() BEFORE it fires this event, so by the time this handler is
      // called a buffered end has already left the device. Reporting it is the
      // difference between "reconnecting to tell it" — which says nothing about
      // whether it ever got told — and "it left, the box has not answered".
      if (this.endPending) {
        this.endPending = false;
        this.opts.onEvent({
          type: 'transport_state', state: 'end_flushed', transport: 'socket.io',
        });
      }
      if (this.started && !this.closing) {
        void this.resumeTransport('socket_reconnect');
      }
    });
    this.socket.on('voice:session_ready', (d: any) => {
      // THE AUTHORITATIVE PAIR RIDES session_ready. It used to be dropped here, so a
      // TRADING window could show Arthur while the box had correctly kept the Rexy
      // call — a surface asserting something it never verified.
      if (d?.call_id) {
        this.call = {
          call_id: String(d.call_id),
          domain: String(d?.domain ?? this.call?.domain ?? ''),
          agent: String(d?.agent ?? this.call?.agent ?? ''),
        };
        this.callId = this.call.call_id;
      }
      this.opts.onEvent({
        type: 'session_ready',
        session_id: String(d?.session_id ?? ''),
        model: String(d?.model ?? ''),
        input_source: d?.input_source ? String(d.input_source) : undefined,
        output_target: d?.output_target ? String(d.output_target) : undefined,
        domain: d?.domain ? String(d.domain) : undefined,
        agent: d?.agent ? String(d.agent) : undefined,
      });
    });
    this.socket.on('voice:transcript_partial', (d: any) => {
      this.opts.onEvent({ type: 'transcript_partial', text: String(d?.text ?? '') });
    });
    this.socket.on('voice:transcript_final', (d: any) => {
      this.opts.onEvent({ type: 'transcript_final', text: String(d?.text ?? ''), turn_key: d?.turn_key ? String(d.turn_key) : undefined });
    });
    this.socket.on('voice:agent_speaking_start', (d: any) => {
      this.opts.onEvent({
        type: 'agent_speaking_start',
        turn_key: String(d?.turn_key ?? ''),
        sentence_idx: Number(d?.sentence_idx ?? 0),
        agent_text: String(d?.agent_text ?? ''),
        overlap: !!d?.overlap,
      });
    });
    this.socket.on('voice:agent_speaking_end', (d: any) => {
      this.opts.onEvent({
        type: 'agent_speaking_end',
        turn_key: String(d?.turn_key ?? ''),
        final_sentence_idx: Number(d?.final_sentence_idx ?? -1),
        delivered_sentence_count: Number(d?.delivered_sentence_count ?? 0),
      });
    });
    this.socket.on('voice:agent_switched', (d: any) => {
      this.opts.onEvent({ type: 'agent_switched', domain: String(d?.domain ?? ''), agent: String(d?.agent ?? ''), turn_key: d?.turn_key ? String(d.turn_key) : undefined });
    });
    this.socket.on('voice:user_barge_in', (d: any) => {
      this.opts.onEvent({ type: 'user_barge_in', turn_key: d?.turn_key ? String(d.turn_key) : undefined });
    });
    this.socket.on('voice:mute_state', (d: any) => {
      // `standing_s` is the box's own measure of how long this mute has stood.
      // Forwarded, never computed here: the box owns the mute, and a surface
      // timing it itself would be a second opinion on one microphone.
      this.opts.onEvent({ type: 'mute_state', muted: !!d?.muted,
                          standing_s: typeof d?.standing_s === 'number'
                            ? d.standing_s : undefined });
      // ── THE MUTE THAT NEVER PASSES THROUGH JAVASCRIPT (build 31) ─────────
      //
      // The lock screen's mute goes device → CallKit → the native `mute` verb,
      // and this window never sees the command at all. On the call being chased
      // the owner toggled six times inside the silent window, and if any of those
      // came from the lock screen a JS-only record would have missed them — while
      // reading as a complete trail, which is worse than a gap.
      //
      // So the ECHO is audited as well as the command. This is the box's own
      // statement of the resulting state and therefore delivered by definition;
      // it says WHAT THE STATE BECAME, on whichever road it was asked for.
      //
      // AFTER the line above, never before: `mute_state` has exactly one writer
      // of `muted` and that ordering is load-bearing. This adds a reader.
      if (this.gainAudited) {
        this.sendGainAudit(gainAuditCommand(d?.muted ? 'mute' : 'unmute', {
          kind: 'echo', delivered: 'yes', detail: 'box_state',
        }));
      }
    });
    this.socket.on('voice:transport_state', (d: any) => {
      // ── THE BOX CONFIRMS OWNERSHIP; CAPABILITY NEVER DID ─────────────────
      //
      // This window stops asserting playback only once the box says a `playing`
      // verb has actually LANDED. The previous gate was the binary's capability
      // flag — "this build can send it" — and the build could: what it could not
      // do was be received, because the edge never imported json and dropped
      // every message. Capability said yes, delivery never happened, and nothing
      // asserted playback at all.
      if (String(d?.state ?? '') === 'playback_peer_owned') {
        this.engine.notePeerOwnsPlayback?.();
      }
      this.opts.onEvent({
        type: 'transport_state',
        state: String(d?.state ?? 'unknown'),
        transport: d?.transport ? String(d.transport) : undefined,
        detail: d?.detail ? String(d.detail) : undefined,
      });
    });
    this.socket.on('voice:session_ended', (d: any) => {
      // Same fact from the other statement — the call is over, so this wrapper
      // stops claiming one. Cleared BEFORE onClose(), because onClose() runs the
      // hook's cleanup and anything reading getCall() on the way past must see
      // the truth rather than the call that just ended.
      this.call = null;
      this.callId = null;
      this.started = false;
      this.endPending = false;
      this.opts.onEvent({ type: 'session_ended', reason: String(d?.reason ?? 'unknown') });
      this.opts.onClose();
    });
    /**
     * ANOTHER WINDOW TOOK THE CALL. This window stops — it does not recover, retry or
     * resume. Supersession kills this window's peer, and the peer's death is exactly
     * what fires scheduleTransportResume; without stopping here, the two windows
     * resume against each other forever. (XMPP <conflict/>: the displaced stream is
     * told and does not blindly reconnect.)
     */
    this.socket.on('voice:call_state', (d: any) => {
      const call = d?.call && d.call.call_id ? (d.call as VoiceCall) : null;
      // THE WRAPPER FORGETS THE CALL WHEN THE BOX SAYS IT IS GONE.
      //
      // `this.call` was written once, by startSession(), and never unwritten. The
      // hook's own `boxCall` cleared on this event, but getCall() went on
      // returning a call that had ended — and getCall() is the gate open() uses to
      // decide whether to end the PREVIOUS session before starting a new one. So a
      // tap meant to START a call emitted user_end_session against a corpse
      // instead: VOICE_B_USER_END_NO_CALL at 20:20:03 and again at 20:20:05 on
      // 2026-08-05, twice, while the owner was trying to get back into a call.
      //
      // The box's word is the only thing that sets this fact, in both directions.
      if (!call) {
        this.call = null;
        this.callId = null;
        this.started = false;
      } else {
        this.call = call;
        this.callId = call.call_id;
      }
      this.opts.onEvent({ type: 'call_state', call });
    });
    this.socket.on('voice:session_superseded', (d: any) => {
      this.superseded = true;
      this.closing = true;
      if (this.reconnectTimer !== null) {
        window.clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      this.opts.onEvent({
        type: 'session_superseded',
        call_id: String(d?.call_id ?? ''),
        reason: String(d?.reason ?? 'another window attached to this call'),
      });
      // Release the device immediately — a superseded window must not keep the mic.
      this.close();
      this.opts.onClose();
    });
    this.socket.on('voice:error', (d: any) => {
      const rawCode = d?.code ?? 500;
      const code = typeof rawCode === 'number' ? rawCode : String(rawCode);
      if (Number(code) === 401) forceAuthLost(this.token);
      this.opts.onEvent({
        type: 'error',
        code,
        turn_key: d?.turn_key ? String(d.turn_key) : undefined,
        reason: String(d?.reason ?? 'unknown'),
        detail: d?.detail ? String(d.detail) : undefined,
        waited_s: d?.waited_s !== undefined ? Number(d.waited_s) : undefined,
      });
    });
    this.socket.on('disconnect', (reason) => {
      if (reason === 'io server disconnect') this.opts.onClose();
      else this.opts.onEvent({
        type: 'transport_state',
        state: 'disconnected',
        transport: 'socket.io',
        detail: String(reason ?? ''),
      });
    });
  }

  private emit(name: string, payload: object): void {
    if (this.socket.connected) this.socket.emit(name, payload);
  }

  private emitAck<T>(name: string, payload: object, timeoutMs = 10000): Promise<T> {
    return new Promise((resolve, reject) => {
      (this.socket as any).timeout(timeoutMs).emit(name, payload, (err: any, response: T) => {
        if (err) reject(err);
        else resolve(response);
      });
    });
  }

  /**
   * THE CLIENT'S ASSERTION — the box cannot see this window's peer, so this window
   * states it. Only a genuinely CONNECTED peer counts: a peer that is still
   * connecting, or one whose connection has failed, is not something the call can be
   * adopted onto. Fail toward REPLACE — a wrong replace costs one renegotiation, a
   * wrong adopt costs silence.
   */
  private hasLivePeer(): boolean {
    return this.engine.isConnected();
  }

  /** IS THERE A CALL? Read-only. Asking attaches nothing and ends nothing. */
  async getCallState(): Promise<VoiceCall | null> {
    const reply = await this.emitAck<{ success?: boolean; call?: VoiceCall | null }>(
      'voice:get_call_state', {});
    const call = reply?.call;
    return call && call.call_id ? call : null;
  }

  /** What the box says is live for this window. Null until it says there is one. */
  getCall(): VoiceCall | null {
    return this.call;
  }

  /**
   * Re-state this window's playback fact after an adopt.
   *
   * The box drops the playback assertion whenever a window detaches, because the window
   * that made it has gone. On an adopt there is no renegotiation and therefore no
   * ontrack, so this is the only place the truth gets restated. Only the audible graph
   * may report ok — a window with no graph reports blocked, which is the safe direction.
   */
  reassertPlayback(): void {
    this.engine.reassertPlayback();
  }

  private async startSession(
    domain: string,
    agent: string,
    createIfAbsent: boolean = this.createIfAbsent,
  ): Promise<{ call: VoiceCall | null; keepMedia: boolean; attached: boolean }> {
    const reply = await this.emitAck<{
      success?: boolean; reason?: string; detail?: string;
      call?: VoiceCall | null; attached?: boolean; keep_media?: boolean;
    }>('voice:session_start', {
      domain,
      agent,
      has_live_peer: this.hasLivePeer(),
      call_id: this.callId,
      create_if_absent: createIfAbsent,
    });
    if (!reply?.success) {
      throw new Error(reply?.detail || reply?.reason || 'voice_session_start_failed');
    }
    // THE BOX'S ANSWER IS THE TRUTH. The call, its id and its {domain, agent} all come
    // from here — learned, never invented, never persisted, never the requested pair.
    const call = reply.call && reply.call.call_id ? reply.call : null;
    this.call = call;
    this.callId = call ? String(call.call_id) : null;
    this.started = !!call;
    return { call, keepMedia: !!reply.keep_media, attached: !!reply.attached };
  }

  /**
   * ABORT-AWARE AFTER EVERY AWAIT (Item 2, second instance).
   *
   * The mic-ownership defect had a twin that was not in the brief. If close() ran
   * while this method was parked on the 15s answer wait, closePeer() stopped the
   * microphone — and then the in-flight negotiation resolved and re-assigned this.pc
   * and this.micStream on an already-closed wrapper. The result was a zombie peer
   * holding a live capture that nothing would ever close again.
   *
   * So: the mic is owned in the same tick getUserMedia resolves, liveness is checked
   * after every single await, and ANY failure tears the peer down rather than leaving
   * a half-negotiated session holding the owner's device.
   */
  private negotiateWebRtc(): Promise<void> {
    // A CONCURRENT CALLER AWAITS THE REAL NEGOTIATION AND SEES ITS OUTCOME.
    // This used to be `if (this.negotiating) return;` — which returned SUCCESS to a
    // caller whose negotiation never ran. Both the socket 'connect' handler and the
    // resume timer can reach here, so one of them could believe it had media when
    // nothing had happened. Sharing the in-flight promise is honest and still runs
    // exactly one negotiation.
    if (this.negotiatePromise) return this.negotiatePromise;
    this.negotiatePromise = this.runNegotiation().finally(() => {
      this.negotiatePromise = null;
    });
    return this.negotiatePromise;
  }

  private async runNegotiation(): Promise<void> {
    this.negotiating = true;
    try {
      // THE ENGINE OWNS THE MEDIA; THIS OWNS THE WIRE. The offer comes back fully
      // gathered and this method's only job between open() and accept() is to
      // carry it to the box and carry the answer back — which is exactly the Q2
      // boundary: signalling never leaves the WebView, and the engine never
      // learns that a socket exists.
      const offer = await this.engine.open();
      const reply = await this.emitAck<{
        success?: boolean;
        reason?: string;
        detail?: string;
        answer?: RTCSessionDescriptionInit;
      }>('voice:webrtc_offer', {
        call_id: this.callId,
        offer: { type: offer.type, sdp: offer.sdp },
      }, 15000);
      this.assertLive('answer');
      if (!reply?.success || !reply.answer) {
        throw new Error(reply?.detail || reply?.reason || 'webrtc_negotiation_failed');
      }
      await this.engine.accept(String(reply.answer.sdp ?? ''));
    } catch (e) {
      // A FAILED NEGOTIATION NEVER KEEPS THE DEVICE. Whether this wrapper survives for
      // a retry or is thrown away, the microphone is released here.
      await this.closePeer().catch(() => {});
      throw e;
    } finally {
      this.negotiating = false;
    }
  }

  /** The engine teardown, with the reason the probe needs. Kept as a method so
   *  every call site reads as it did before the lift. */
  private closePeer(): Promise<void> {
    return this.engine.close(
      this.superseded ? 'superseded' : this.closing ? 'closing' : 'renegotiate');
  }

  private scheduleTransportResume(reason: string): void {
    // A SUPERSEDED WINDOW NEVER RESUMES. This is the half without which the ping-pong
    // returns under any attach policy.
    if (this.reconnectTimer !== null || this.closing || this.superseded) return;
    this.opts.onEvent({ type: 'transport_state', state: 'reconnecting', transport: 'webrtc', detail: reason });
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      void this.resumeTransport(reason);
    }, 750);
  }

  /**
   * THE APP CAME BACK (Fix 3, owner-ruled 2026-08-10).
   *
   * The public door onto the EXISTING recovery path — no second route, no new
   * mechanism. `resumeTransport` is private and stays private; this exists
   * because the listener lives in the hook that owns the wrapper, and a
   * foreground return is a fact only the surface can observe.
   *
   * INSTRUMENTATION BEFORE MECHANISM. The edge is recorded BEFORE the attempt,
   * carrying `socket_connected` — because `resumeTransport` returns immediately
   * when the socket is down, and a null result would otherwise read exactly like
   * a recovery that was not needed. The owner's own audit already shows an adopt
   * and a fresh offer beginning before the call was ended, so the honest question
   * is whether the existing path completes on its own; this is what will answer
   * it. If it does, Fix 3 collapses to this trigger and nothing more.
   */
  resumeFromForeground(event: string): void {
    if (this.closing || this.superseded || !this.started) return;
    this.opts.onEvent({
      type: 'transport_state',
      state: 'foreground',
      transport: 'app',
      detail: `event=${event} socket_connected=${this.socket.connected}`
        + ` has_live_peer=${this.hasLivePeer()}`,
    });
    void this.resumeTransport('foreground');
  }

  private async resumeTransport(reason: string): Promise<void> {
    if (this.closing || this.superseded || !this.socket.connected) return;
    try {
      // EVERY RECONNECT IS AN ATTACH. If this window's peer survived the socket blip,
      // the box keeps it and the media is never touched — the call does not drop. A
      // reconnect attaches to whatever the box has; it never creates a call, so a
      // reconnect arriving after the call ended does not silently start a new one.
      const attach = await this.startSession(this.opts.domain, this.opts.agent, false);
      if (!attach.call) {
        // The call is gone. A reconnect must not resurrect one — the sphere ends calls,
        // not a transport timer.
        this.opts.onEvent({ type: 'session_ended', reason: 'call_no_longer_live' });
        this.opts.onClose();
        return;
      }
      // WHAT THE ATTACH DECIDED (Fix 3). `keep_media` is computed at the box
      // from `has_live_peer`, which is this window's CACHED view of its own
      // peer — a value that may have missed its update while the web content
      // process was suspended. A stale true adopts and never renegotiates,
      // which is silence, so the decision is recorded rather than inferred from
      // whether audio came back.
      if (reason === 'foreground') {
        this.opts.onEvent({
          type: 'transport_state',
          state: 'foreground_attached',
          transport: 'app',
          detail: `keep_media=${attach.keepMedia}`
            + ` renegotiating=${!attach.keepMedia}`,
        });
      }
      if (!attach.keepMedia) await this.negotiateWebRtc();
    } catch (e) {
      this.opts.onEvent({
        type: 'error',
        code: 'WEBRTC_RECONNECT_FAILED',
        reason,
        detail: String((e as Error)?.message ?? e),
      });
      this.scheduleTransportResume('retry_after_failure');
    }
  }

  // RETIRED — sendChunk() and playbackAck() are GONE (Sherman F1, owner-ruled 2026-07-29).
  //
  // sendChunk() carried the owner's microphone over a data channel and, when that channel
  // was not open, reported "holding" and RETURNED — the audio was neither queued nor
  // replayed. It was simply lost, which fails "lose nothing" by construction. The mic now
  // rides an RTP track added at negotiation: it is carried continuously, with sequence
  // numbers, timestamps and packet-loss concealment, and there is no code path left on
  // which a chunk can be silently discarded. Nothing replaced it because nothing needs to.
  //
  // playbackAck() asserted that a frame had reached the owner. The browser cannot verify
  // that — it knew only that bytes had arrived. The heard boundary is now measured where
  // the audio actually leaves the box (AgentAudioTrack acks a mark when its samples are
  // emitted into RTP), so the browser must not claim it. The server refuses a browser ack
  // with a typed error rather than honouring it.

  /**
   * BARGE-IN AT THE BROWSER — a DROP, never a drain. The box has already stopped
   * emitting and discarded its pending marks; this closes the last gap, the audio
   * already inside playout, so nothing can play over the owner.
   */
  duckPlayback(turnKey?: string): void { this.engine.duck(turnKey); }

  /** The next turn's audio is starting — restore the audible path. */
  resumePlayback(turnKey?: string): void { this.engine.resume(turnKey); }

  /**
   * The microphone stream ALREADY negotiated onto the peer connection. The meter reads
   * this rather than opening a second capture — one device, one AEC instance.
   */
  getMicStream(): MediaStream | null { return this.engine.micStream(); }

  /** Gate the engine's level poll. Null-safe: the browser engine does not
   *  implement it because its level is free. */
  setLevelReporting(enabled: boolean): void { this.engine.setLevelReporting?.(enabled); }

  mute(): void { this.emit('voice:mute', {}); this.auditMute('mute'); }
  unmute(): void { this.emit('voice:unmute', {}); this.auditMute('unmute'); }

  /** ── THE OTHER MECHANISM THAT DRIVES THE GAIN (build 31) ────────────────
   *
   *  A duck that never lifted and a mute that never cleared produce the same
   *  silence, and until now neither was audited — which is why three read-only
   *  rounds could not separate them. This is the mute half, and it records the
   *  fact the duck rows cannot supply: that the owner asked, and when.
   *
   *  THE TAP IS SENT FIRST. This runs after `emit`, never before it, so the
   *  witness cannot delay the command it is witnessing.
   *
   *  `delivered` IS READ FROM THE SAME CONDITION `emit` USED. That private
   *  helper drops the packet when the socket is down — quietly, and it has done
   *  so for the whole life of this file. Nothing here changes that; it makes it
   *  VISIBLE, which is the difference between a mute that failed and a mute that
   *  failed invisibly. */
  private auditMute(cause: 'mute' | 'unmute'): void {
    if (!this.gainAudited) return;
    const delivered = this.socket.connected;
    this.sendGainAudit(gainAuditCommand(cause, {
      delivered: delivered ? 'yes' : 'no',
      detail: delivered ? '' : 'socket_disconnected',
    }));
  }

  /** ── EVERY CROSSING REPORTS ITS OWN FAILURE ─────────────────────────────
   *
   *  A gain row that silently failed to send would leave the next occurrence
   *  exactly as unprovable as the one that prompted this round, so a row that
   *  cannot leave is COUNTED and the count rides the next row that lands.
   *
   *  IT IS NOT RETRIED AND NOT BUFFERED. socket.io would queue an emit made
   *  while disconnected and flush the backlog on reconnect; a per-second
   *  diagnostic doing that would arrive as a burst, timestamped wrong, competing
   *  for the wire with the call trying to recover. A diagnostic that changes the
   *  failure it is measuring is worse than no diagnostic. So this uses the
   *  socket directly rather than `emit()` — same connected test, no queue.
   *
   *  REPORTED ONCE PER GAP, not once per lost row: the readout says the trail
   *  has broken, and the count says how badly. A transport event per dropped row
   *  would be a second flood in place of the first. Never an error — an
   *  undelivered diagnostic must not invite a tap. */
  private sendGainAudit(row: GainAuditRow): void {
    if (!this.gainAudited) return;
    if (!this.socket.connected) {
      // ONCE PER GAP, AND NOW ACTUALLY ONCE. Nothing clears the count until a row
      // leaves, so a return of exactly 1 is the first loss of a gap. It used to be
      // true on every loss, because the count was cleared when a row was COMPOSED
      // — the comment claimed a property the code did not have, which in this lane
      // is its own category of fault.
      if (gainAuditUndelivered() === 1) this.reportGainAuditGap(row, 'socket_disconnected');
      return;
    }
    try {
      // ── WHICH ENGINE RAN, STATED RATHER THAN INFERRED (Gap 4, 2026-08-14) ──
      //
      // Across 67 recorded calls the trail can identify the media engine for ten
      // — and only by inference: gain rows exist, `gainAudited` is
      // `engine instanceof NativeMediaEngine`, therefore native. That inference
      // is correct today and is not a fact anyone wrote down, and it EXPIRES the
      // moment the audit runs for both engines. In a four-platform architecture
      // — desktop, tablet, phone, iOS and Android — "which engine was this?" is
      // the first question of every diagnosis, and it should not be answered by
      // reasoning about the absence of rows.
      //
      // DERIVED FROM THE ENGINE, NOT FROM `gainAudited`. A ternary on
      // `gainAudited` would read as adaptive and could never return 'browser',
      // because this method returns early unless it is true — a tautology wearing
      // a derivation's clothes, which is worse than a literal. This asks the same
      // question `gainAudited` was built from, at the same site, so it stays true
      // if the emit ever becomes reachable for the browser engine.
      //
      // WHAT THIS DOES NOT CLOSE: a browser-engine call still emits NO rows, so
      // its engine remains unrecorded. This makes the rows that exist
      // self-describing; it does not make the silent surfaces visible.
      //
      // STAMPED ONTO THE ROW, AND THE EMIT'S ARGUMENT DOES NOT MOVE. Two
      // assertions in mediaProbe.test.mts pin `emit('voice:gain_audit', row)` —
      // one for "no new verb on the control channel", one for "the debt is
      // cleared where it is settled". Both properties still hold here, and
      // rewriting the pins to admit a spread would loosen two live guards to
      // carry one field. The row is the thing being described, so the row carries
      // the field.
      row = { ...row, engine: this.engine instanceof NativeMediaEngine ? 'native' : 'browser' };
      this.socket.emit('voice:gain_audit', row);
      // THE DEBT IS PAID WHERE IT IS SETTLED, not where it is written down: this
      // row has left carrying `row.undelivered` lost ones, and only now do they
      // stop being owed.
      gainAuditDelivered(row.undelivered);
    } catch {
      // THE SECOND LOSS PATH, AND IT REPORTS LIKE THE FIRST (Sherman, round 2).
      // This used to be a bare increment. A gap that BEGAN with a throwing emit
      // therefore never reached the readout at all, and a disconnect after it
      // returned 2 and stayed silent — the same "two paths, one of which
      // reports" shape as the defect this round already fixed, one step further
      // out. Every path that loses a row now runs the identical gap test.
      if (gainAuditUndelivered() === 1) this.reportGainAuditGap(row, 'emit_threw');
    }
  }

  /** ── THE GAP IS ANNOUNCED ONCE, AND THE ANNOUNCEMENT CANNOT BECOME THE FAULT.
   *
   *  Called only where `gainAuditUndelivered()` has just returned 1 — i.e. at the
   *  START of a gap, never inside one. `why` names which crossing failed, because
   *  a socket that is down and a socket that threw are different findings and the
   *  readout should not merge them.
   *
   *  A CONSUMER THAT THROWS MUST NOT REACH THE TAP. This is reachable from
   *  `mute()`/`unmute()` with the socket down, and those run inside the owner's
   *  tap: an exception here would abort the tap path on its way out. */
  private reportGainAuditGap(row: GainAuditRow, why: string): void {
    try {
      this.opts.onEvent({
        type: 'transport_state', state: 'gain_audit_undelivered', transport: 'socket',
        detail: `seq=${row.seq} kind=${row.kind} cause=${row.cause} why=${why}`,
      });
    } catch { /* the report is the diagnostic; it may not become the fault */ }
  }

  /**
   * Returns whether the end actually REACHED the box.
   *
   * NEVER PRETEND SOMETHING WAS DELIVERED WHEN IT WASN'T. If the socket is not
   * connected the emit is dropped, and the caller has to be able to say so rather than
   * report a clean end that the server never heard.
   */
  /**
   * THE TAP. It must reach the box, and a dead socket is not an excuse.
   *
   * This used to `return false` the instant `socket.connected` was false, and the
   * caller then tore the socket down — destroying the only channel that could
   * have delivered the end. On 2026-08-05 that is exactly what happened: the
   * socket had already dropped (WINDOW_DETACHED, reason: socket), the tap emitted
   * NOTHING, and the audit carries no USER_END for it. The call stayed live and
   * the banner told the owner to tap a button wired to nothing.
   *
   * Now the emit ALWAYS happens. socket.io buffers a packet emitted while
   * disconnected and flushes it on connect, so the ordering that matters is
   * emit-then-reconnect, never reconnect-then-hope. The boolean still means
   * "delivered NOW", because the caller has to be able to say so honestly — it
   * no longer means "attempted".
   */
  endSession(): boolean {
    this.closing = true;
    try { this.socket.emit('voice:user_end_session', {}); } catch { /* buffered or lost */ }
    if (this.socket.connected) { this.endPending = false; return true; }
    // Buffered, not sent. The flag is what lets the connect handler above report
    // the moment it actually leaves.
    this.endPending = true;
    // Dead or dropping: bring it back so the buffered packet can leave. A tap is
    // deliberate, so reconnecting to deliver it is not the automatic recovery the
    // superseded-window rule forbids — it carries a human decision, nothing else.
    try { this.socket.connect(); } catch { /* nothing more this side can do */ }
    return false;
  }

  /**
   * RELEASE THE DEVICE, KEEP THE CHANNEL.
   *
   * The mic must never outlive the tap that ended the call — but the SOCKET is
   * what carries the end to the box, and closing both together is what made an
   * undelivered end unrecoverable. This drops the peer and the microphone and
   * leaves the socket alive to deliver and to hear the box's answer.
   */
  releaseMedia(): void {
    void this.closePeer();
  }

  close(): void {
    this.closing = true;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    // Anything parked on the start path stops NOW rather than waiting out its timeout.
    const waiters = this.abortWaiters;
    this.abortWaiters = [];
    waiters.forEach((reject) => {
      try { reject(new Error('voice_start_cancelled')); } catch {}
    });
    void this.closePeer();
    try { this.socket.removeAllListeners(); } catch {}
    try { this.socket.disconnect(); } catch {}
  }
}
